import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    TextField,
    Button,
    Paper,
    Alert,
    CircularProgress,
    Container
} from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { styled } from '@mui/system';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import fcmService from './services/fcmService';

const FormContainer = styled(Paper)({
    padding: '2rem',
    maxWidth: '400px',
    margin: '2rem auto',
    borderRadius: '12px',
    boxShadow: '0 3px 10px rgba(0, 0, 0, 0.1)',
});

const StyledTextField = styled(TextField)({
    marginBottom: '1rem',
    '& .MuiOutlinedInput-root': {
        '&:hover fieldset': {
            borderColor: '#10B981',
        },
        '&.Mui-focused fieldset': {
            borderColor: '#10B981',
        },
    },
    '& .MuiInputLabel-root.Mui-focused': {
        color: '#10B981',
    },
});

const SubmitButton = styled(Button)({
    backgroundColor: '#10B981',
    color: 'white',
    padding: '0.75rem',
    fontSize: '1rem',
    fontWeight: 500,
    '&:hover': {
        backgroundColor: '#059669',
    },
    '&:disabled': {
        backgroundColor: '#94A3B8',
    },
});

const GuestForm = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const valetId = searchParams.get('valetId');
    const mode = searchParams.get('mode') || 'park';
    
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        phoneNumber: ''
    });
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [generalError, setGeneralError] = useState('');
    const [notificationPermission, setNotificationPermission] = useState(
        'Notification' in window ? Notification.permission : 'unsupported'
    );

    useEffect(() => {
        if (!valetId) {
            setGeneralError('Invalid QR code. Please scan a valid valet QR code.');
        }
    }, [valetId]);

    const validateForm = () => {
        const newErrors = {};
        
        if (!formData.firstName.trim()) {
            newErrors.firstName = 'First name is required';
        }
        
        if (!formData.lastName.trim()) {
            newErrors.lastName = 'Last name is required';
        }
        
        const phoneRegex = /^\d{10}$/;
        if (!phoneRegex.test(formData.phoneNumber.replace(/\D/g, ''))) {
            newErrors.phoneNumber = 'Please enter a valid 10-digit phone number';
        }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const formatPhoneNumber = (value) => {
        const numbers = value.replace(/\D/g, '');
        if (numbers.length <= 3) return numbers;
        if (numbers.length <= 6) return `(${numbers.slice(0, 3)}) ${numbers.slice(3)}`;
        return `(${numbers.slice(0, 3)}) ${numbers.slice(3, 6)}-${numbers.slice(6, 10)}`;
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        
        if (name === 'phoneNumber') {
            const formatted = formatPhoneNumber(value);
            setFormData(prev => ({ ...prev, [name]: formatted }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
        
        // Clear error for this field when user starts typing
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };
    
    const handleRequestNotifications = async () => {
        console.log('GuestForm: User clicked request notifications button');
        const permission = await fcmService.requestNotificationPermission();
        setNotificationPermission(permission);
        
        if (permission === 'granted') {
            console.log('GuestForm: Permission granted, attempting to get FCM token...');
            const token = await fcmService.getToken();
            if (token) {
                console.log('GuestForm: FCM token obtained after manual permission');
            }
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setGeneralError('');
        
        if (!validateForm()) {
            return;
        }
        
        setLoading(true);
        
        try {
            // Import guestService dynamically to avoid circular dependencies
            const { findGuestByPhone, createGuestUser, updateGuestUser, findActiveConversation, createGuestConversation, reopenConversationForRetrieval } = 
                await import('./services/guestService');
            
            const cleanPhone = formData.phoneNumber.replace(/\D/g, '');
            
            // Get FCM token (only if permission already granted)
            let fcmToken = null;
            try {
                console.log('GuestForm: Checking FCM token...');
                // Only try to get token if permission is already granted
                if (notificationPermission === 'granted') {
                    console.log('GuestForm: Permission already granted, getting FCM token...');
                    fcmToken = await fcmService.getToken();
                    console.log('GuestForm: FCM token result:', fcmToken ? 'Token obtained' : 'No token');
                } else {
                    console.log('GuestForm: Notification permission not granted, skipping FCM token');
                }
            } catch (fcmError) {
                console.error('GuestForm: Error getting FCM token:', fcmError);
                // Continue without FCM token - don't block form submission
            }
            
            // Always check for existing conversation first
            const existingConversation = await findActiveConversation(cleanPhone);
            
            if (existingConversation) {
                // Found an existing conversation
                // Update the guest user info if we have a guestUserId
                if (existingConversation.guestUserId) {
                    const updates = {
                        firstName: formData.firstName,
                        lastName: formData.lastName
                    };
                    
                    // Only update FCM token if we have one
                    if (fcmToken) {
                        updates.fcmToken = fcmToken;
                    }
                    
                    console.log('GuestForm: Updating guest user with:', updates);
                    console.log('GuestForm: Guest user ID:', existingConversation.guestUserId);
                    await updateGuestUser(existingConversation.guestUserId, updates);
                }
                
                // Store guest info in storage for ActiveConversation
                const guestUserToStore = {
                    firstName: formData.firstName,
                    lastName: formData.lastName,
                    phoneNumber: cleanPhone,
                    isGuest: true,
                    id: existingConversation.guestUserId
                };
                sessionStorage.setItem('guestUser', JSON.stringify(guestUserToStore));
                localStorage.setItem('guestUser', JSON.stringify(guestUserToStore));

                sessionStorage.setItem(
                    'guestConversationId',
                    existingConversation.id
                );
                localStorage.setItem('guestConversationId', existingConversation.id);
                
                // If car is parked and conversation is not active, reopen it
                if (existingConversation.carStatus === 'parked' && 
                    existingConversation.status !== 'active' && 
                    existingConversation.status !== 'pending') {
                    await reopenConversationForRetrieval(existingConversation.id);
                    
                    // Navigate to waiting screen for retrieval
                    sessionStorage.setItem('guestIsRetrieval', 'true');
                    localStorage.setItem('guestIsRetrieval', 'true');
                    navigate('/waiting-for-valet', {
                        state: { 
                            conversationId: existingConversation.id,
                            isGuest: true,
                            isRetrieval: true
                        }
                    });
                } else if (existingConversation.status === 'pending') {
                    // Already pending - go to waiting screen
                    sessionStorage.setItem(
                        'guestIsRetrieval',
                        String(!!existingConversation.isRetrievalRequest)
                    );
                    localStorage.setItem(
                        'guestIsRetrieval',
                        String(!!existingConversation.isRetrievalRequest)
                    );
                    navigate('/waiting-for-valet', {
                        state: { 
                            conversationId: existingConversation.id,
                            isGuest: true,
                            isRetrieval: existingConversation.isRetrievalRequest || false
                        }
                    });
                } else {
                    // Active conversation - go directly to chat
                    navigate('/active-chat', {
                        state: { 
                            conversationId: existingConversation.id, 
                            valetId: existingConversation.valetId,
                            isGuest: true 
                        }
                    });
                }
            } else {
                // No existing conversation found - create new one
                let guestUser = await findGuestByPhone(cleanPhone);
                
                if (!guestUser) {
                    // Create new guest user with FCM token
                    const userId = await createGuestUser(
                        formData.firstName,
                        formData.lastName,
                        cleanPhone,
                        fcmToken
                    );
                    guestUser = {
                        id: userId,
                        firstName: formData.firstName,
                        lastName: formData.lastName,
                        phoneNumber: cleanPhone
                    };
                } else {
                    // Update existing user with new info and FCM token
                    const updates = {
                        firstName: formData.firstName,
                        lastName: formData.lastName
                    };
                    
                    // Only update FCM token if we have one
                    if (fcmToken) {
                        updates.fcmToken = fcmToken;
                    }
                    
                    console.log('GuestForm: Updating existing guest user with:', updates);
                    console.log('GuestForm: Existing guest user ID:', guestUser.id);
                    await updateGuestUser(guestUser.id, updates);
                    
                    // Update local guestUser object
                    guestUser = {
                        ...guestUser,
                        ...updates
                    };
                }
                
                // Create conversation
                const conversationId = await createGuestConversation(guestUser, valetId);

                sessionStorage.setItem('guestConversationId', conversationId);
                localStorage.setItem('guestConversationId', conversationId);
                
                // Store guest info in session storage
                const guestUserToStore = { ...guestUser, isGuest: true };
                sessionStorage.setItem('guestUser', JSON.stringify(guestUserToStore));
                localStorage.setItem('guestUser', JSON.stringify(guestUserToStore));
                sessionStorage.setItem('guestIsRetrieval', 'false');
                localStorage.setItem('guestIsRetrieval', 'false');
                
                // Navigate to waiting screen for new conversations
                navigate('/waiting-for-valet', {
                    state: { 
                        conversationId,
                        isGuest: true,
                        isRetrieval: false
                    }
                });
            }
        } catch (error) {
            console.error('Error processing guest form:', error);
            setGeneralError('An error occurred. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Container maxWidth="sm">
            <Box sx={{ mt: 4, mb: 4 }}>
                <FormContainer>
                    <Box sx={{ textAlign: 'center', mb: 3 }}>
                        <DirectionsCarIcon sx={{ fontSize: 48, color: '#10B981', mb: 2 }} />
                        <Typography variant="h4" sx={{ color: '#1E293B', fontWeight: 600 }}>
                            Valet Service
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#64748B', mt: 1 }}>
                            Please provide your information to access valet service
                        </Typography>
                    </Box>

                    {generalError && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {generalError}
                        </Alert>
                    )}
                    
                    {/* Show notification permission prompt if not granted */}
                    {notificationPermission === 'default' && (
                        <Alert 
                            severity="info" 
                            sx={{ mb: 2 }}
                            action={
                                <Button 
                                    color="inherit" 
                                    size="small"
                                    onClick={handleRequestNotifications}
                                >
                                    Enable
                                </Button>
                            }
                        >
                            Enable notifications to receive updates from your valet
                        </Alert>
                    )}
                    
                    {notificationPermission === 'denied' && (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            Notifications are blocked. To receive updates, enable notifications in your browser settings.
                        </Alert>
                    )}

                    <form onSubmit={handleSubmit}>
                        <StyledTextField
                            fullWidth
                            label="First Name"
                            name="firstName"
                            value={formData.firstName}
                            onChange={handleInputChange}
                            error={!!errors.firstName}
                            helperText={errors.firstName}
                            InputProps={{
                                startAdornment: <PersonIcon sx={{ mr: 1, color: '#94A3B8' }} />
                            }}
                        />

                        <StyledTextField
                            fullWidth
                            label="Last Name"
                            name="lastName"
                            value={formData.lastName}
                            onChange={handleInputChange}
                            error={!!errors.lastName}
                            helperText={errors.lastName}
                            InputProps={{
                                startAdornment: <PersonIcon sx={{ mr: 1, color: '#94A3B8' }} />
                            }}
                        />

                        <StyledTextField
                            fullWidth
                            label="Phone Number"
                            name="phoneNumber"
                            value={formData.phoneNumber}
                            onChange={handleInputChange}
                            error={!!errors.phoneNumber}
                            helperText={errors.phoneNumber}
                            placeholder="(123) 456-7890"
                            InputProps={{
                                startAdornment: <PhoneIcon sx={{ mr: 1, color: '#94A3B8' }} />
                            }}
                        />

                        <SubmitButton
                            type="submit"
                            fullWidth
                            variant="contained"
                            disabled={loading || !valetId}
                            sx={{ mt: 2 }}
                        >
                            {loading ? (
                                <CircularProgress size={24} sx={{ color: 'white' }} />
                            ) : (
                                'Continue'
                            )}
                        </SubmitButton>
                    </form>
                </FormContainer>
            </Box>
        </Container>
    );
};

export default GuestForm;